// React, Third Party Libraries
import React, { FormEvent, useEffect, useState } from 'react'
import { Form, useNavigate } from 'react-router-dom';
import { Alert, Button, IconButton, InputLabel, MenuItem, Select, TextField } from '@mui/material';
import { useConfirm } from 'material-ui-confirm';

import { ISelectOption } from '../../types/list';
import { ITransporterDetails } from '../../types/transporter';
import { IFuzzySearchResult } from '../../types/system';

import { AppConfig } from '../../config/appConfig';
import { ErrorProvider } from '../../classes/errorProvider';
import { useListContext } from '../../contexts/listContext';

import { TransporterService } from '../../services/transporterService';
import { UtilityService } from '../../services/utilityService';

import { Icons } from '../icons';

import '../../styles/transporterDetailsForm.scss';

export interface ITransporterDetailsForm {
	action: "add" | "edit";
	id?: string;
	details: ITransporterDetails;
	reloadData: () => void;
}

const TransporterDetailsForm: React.FunctionComponent<ITransporterDetailsForm> = ({ action, id, details, reloadData }) => {
	const confirm = useConfirm();
	const listContext = useListContext();
	const navigate = useNavigate();
	const [statusOptions, setStatusOptions] = useState<ISelectOption[]>([{ name: "Active", value: "1" }, { name: "Inactive", value: "2" }]);
	const [formFields, setFormFields] = useState<ITransporterDetails>(details);
	const [originalTransporterName, setOriginalTransporterName] = useState<string>(details?.name);
	const [isSaving, setIsSaving] = useState(false);
	const [isCheckingNameForDupes, setIsCheckingNameForDupes] = useState(false);
	const [errorMessage, setErrorMessage] = useState<string | undefined>();
	const [successMessage, setSuccessMessage] = useState<string | undefined>();


	useEffect(() => {
		if (!formFields.statusId) setFormFields({ ...formFields, ["statusId"]: "1" })
	}, [details])

	const doSave = async () => {
		clearSaveResult();
		setIsSaving(true);

		if (action == "add") {
			TransporterService.addTransporter(formFields)
				.then((response) => {
					console.log('TransporterService.addTransporter: SUCCESS', response);
					setSuccessMessage("Transporter has been added.");
					setTimeout(() => {
						navigate("/transporter/" + response.data.id);
					}, AppConfig.addSuccessToastMs);
				})
				.catch((error) => {
					console.log('TransporterService.addTransporter: ERROR', error);
					setErrorMessage(ErrorProvider.getError(error));
				})
				.finally(() => {
					setIsSaving(false);
					window.scrollTo(0, 0);
				});
		}
		else if (action == "edit" && id) {
			TransporterService.setDetails(id, formFields)
				.then((response) => {
					console.log('TransporterService.setDetails: SUCCESS', response);
					reloadData();
					setSuccessMessage("Transporter details have been saved.");
					setTimeout(() => {
						setSuccessMessage(undefined);
					}, AppConfig.editSuccessToastMs);
				})
				.catch((error) => {
					console.log('TransporterService.setDetails: ERROR', error);
					setErrorMessage(ErrorProvider.getError(error));
				})
				.finally(() => {
					setIsSaving(false);
					window.scrollTo(0, 0);
				});
		}
	}

const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
		// Fuzzy check existing transporter names for potential duplicates before saving		
		event.preventDefault();
		setErrorMessage(undefined);

		// If the name wasn't changed then don't bother checking for duplicates
		if (action == "edit" && formFields?.name == originalTransporterName) {
			await doSave();
			return;
		}
		
		setIsCheckingNameForDupes(true);

		TransporterService.fuzzySearchForDuplicateTransporterNames({ name: formFields?.name, excludeId: action == "edit" ? +id : undefined })
			.then(async (response) => {
				console.log('TransporterService.fuzzySearchForDuplicateTransporterNames: SUCCESS', response);
				const matches: IFuzzySearchResult[] = response.data;
				if (matches.length > 0) {
					if (await UtilityService.ConfirmationModal(
						confirm,
						<div className="dupes-dialog-title">
							<Icons.Warning fill="#272f50" height="14px"></Icons.Warning>
							Potential Duplicates
						</div>,
						<div className="dupes-dialog-content">
							The name <strong>{formFields.name}</strong> is similar to <strong>{matches.length}</strong> existing transporter{matches.length > 1 ? "s" : ""}. Please review the conflicts below before proceeding.<br />
							<ul>
								{matches.map((item, index) => (
									<li key={index}>
										<a href={`/transporter/${item.id}`} target="_blank" rel="noopener noreferrer">
											<Icons.ViewItem height="14px"></Icons.ViewItem>
											{item.name}
										</a>
									</li>
								))}
							</ul>
						</div>,
						{
							confirmationText: "Continue",
							confirmationButtonProps: { color: 'primary', variant: 'contained' },
							cancellationText: "Cancel"
						}
					) == false) {
						return;
					}
				}
				
				await doSave();
			})
			.catch((error) => {
				console.log('TransporterService.fuzzySearchForDuplicateTransporterNames: ERROR', error);
				setErrorMessage(ErrorProvider.getError(error));
			})
			.finally(() => {
				setIsCheckingNameForDupes(false);
			});
	}


	const clearSaveResult = () => {
		setErrorMessage(undefined);
		setSuccessMessage(undefined);
	};

	return (
		<section className="hwi-transporter-details-form">
			{successMessage &&
				<Alert className="alert-success"
					severity="success"
					action={<IconButton onClick={() => { setSuccessMessage(undefined); }} size='small'>X</IconButton>}>
					{successMessage}
				</Alert>
			}
			{errorMessage &&
				<Alert className="alert-error"
					severity="error"
					action={<IconButton onClick={() => { setErrorMessage(undefined); }} size='small'>X</IconButton>}>
					{errorMessage}
				</Alert>
			}
			<Form onSubmit={(event) => handleSubmit(event)}>
				<div className="row">
					<InputLabel>Name *</InputLabel>
					<TextField className="name-field" required name="name" value={formFields.name} size="small" onChange={(event) => { setFormFields({ ...formFields, ["name"]: event.target.value }) }}></TextField>
				</div>
				<div className="row">
					<InputLabel>DOT Number *</InputLabel>
					<TextField className="name-field" required name="name" value={formFields.dotNumber} size="small" onChange={(event) => { setFormFields({ ...formFields, ["dotNumber"]: event.target.value }) }}></TextField>
				</div>
				<div className="row">
					<InputLabel>Status</InputLabel>
					<Select
						value={formFields.statusId}
						className="brand-field"
						onChange={(event) => { setFormFields({ ...formFields, ["statusId"]: event.target.value }) }}
					>
						{statusOptions.map((item, index) => (
							<MenuItem value={item.value} key={index}>{item.name}</MenuItem>
						))}
					</Select>
				</div>
				<div className="row">
					<InputLabel>EPA ID</InputLabel>
					<TextField className="name-field"
						name="epaId"
						value={formFields.epaId}
						size="small"
						onChange={(event) => { setFormFields({ ...formFields, ["epaId"]: event.target.value }) }}
						InputProps={{ inputProps: { maxLength: 12 } }}>
					</TextField>
				</div>
				<div className="row controls">
					<Button type="submit" className="save-button" disabled={isSaving} variant="contained">{isSaving ? "Saving..." : "Save Details"}</Button>
				</div>
			</Form>
			{/* <Json data={formFields}></Json> */}
		</section>
	)
}

export default TransporterDetailsForm;